import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { IProductionDashboardStepDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import IUser from "../../models/IUser";
import { IMachineStationOption } from "../../models/ISupplierSettings";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";

interface AdvanceStepModalProps {
    show: boolean;
    selectedCount: number;
    currentStep: IProductionDashboardStepDTO | null;
    nextStep: IProductionDashboardStepDTO | null;
    employees: IUser[];
    defaultEmployeeId: number | null;
    isAdmin: boolean;
    isAdvancing: boolean;
    errorMessage: string | null;
    machineStations?: IMachineStationOption[];
    onConfirm: (employeeUserId: number | undefined, machineStation: string, notes: string) => void;
    onCancel: () => void;
}

const AdvanceStepModal: React.FC<AdvanceStepModalProps> = ({
    show,
    selectedCount,
    currentStep,
    nextStep,
    employees,
    defaultEmployeeId,
    isAdmin,
    isAdvancing,
    errorMessage,
    machineStations,
    onConfirm,
    onCancel,
}) => {
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(defaultEmployeeId ?? undefined);
    const [machineStation, setMachineStation] = useState("");
    const [notes, setNotes] = useState("");

    const handleConfirm = () => {
        onConfirm(selectedEmployeeId, machineStation, notes);
    };

    const handleExited = () => {
        setMachineStation("");
        setNotes("");
    };

    return (
        <Modal show={show} onHide={onCancel} onExited={handleExited} centered size="lg" className="sf-advance-modal">
            <Modal.Header closeButton>
                <Modal.Title>Confirm Step Completion</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="sf-advance-modal__summary mb-3">
                    <strong>{selectedCount}</strong> serial{selectedCount !== 1 ? "s" : ""}
                    {currentStep && nextStep && (
                        <>
                            {": "}
                            <span className="sf-advance-modal__step-name">
                                <OwTranslate id={currentStep.stepName} defaultMessage={currentStep.stepName} />
                            </span>
                            {" → "}
                            <span className="sf-advance-modal__step-name">
                                <OwTranslate id={nextStep.stepName} defaultMessage={nextStep.stepName} />
                            </span>
                        </>
                    )}
                    {currentStep && !nextStep && (
                        <>
                            {": completing "}
                            <span className="sf-advance-modal__step-name">{currentStep.stepName}</span>
                            {" (final step)"}
                        </>
                    )}
                </div>

                {errorMessage && <div className="alert alert-danger mb-3">{errorMessage}</div>}

                {isAdmin && (
                    <div className="mb-3">
                        <label className="form-label" htmlFor="sf-advance-user">
                            User
                        </label>
                        <select
                            id="sf-advance-user"
                            className="form-select"
                            value={selectedEmployeeId ?? ""}
                            onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : undefined)}>
                            <option value="">Select user...</option>
                            {employees.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="mb-3">
                    <label className="form-label" htmlFor="sf-advance-machine">
                        Machine / Station (optional)
                    </label>
                    {machineStations && machineStations.length > 0 ? (
                        <OwSelectOption
                            id="sf-advance-machine"
                            name="machineStation"
                            options={machineStations}
                            valueProp={"value"}
                            titleProp={"title"}
                            selectedOption={machineStation}
                            hasSelectedOptionProp={false}
                            showBlankFirst={true}
                            sizeClass={"reg"}
                            fn={(e: React.ChangeEvent<HTMLSelectElement>) => setMachineStation(e.target.value)}
                        />
                    ) : (
                        <input
                            id="sf-advance-machine"
                            type="text"
                            className="form-control"
                            value={machineStation}
                            onChange={(e) => setMachineStation(e.target.value)}
                            placeholder="e.g. Welder 3, Station B"
                        />
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label" htmlFor="sf-advance-notes">
                        Notes (optional)
                    </label>
                    <textarea
                        id="sf-advance-notes"
                        className="form-control"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any notes about this batch..."
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel} disabled={isAdvancing}>
                    Cancel
                </Button>
                <Button variant="success" onClick={handleConfirm} disabled={isAdvancing}>
                    {isAdvancing ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Advancing...
                        </>
                    ) : (
                        "Confirm"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AdvanceStepModal;
